import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import api from "../../services/api";
import ModalCustom from "./modal-template-default";
import useWrapper from "../../hooks/useWrapper";
import { toast } from "react-toastify";
import Results from "../Results";
import StatusFlag from "../StatusFlag";
import useFormat from "../../hooks/utils/useFormat";
import { InputText } from "../Input";
import Button from "../Button";
import usePermission from "../../hooks/usePermission";

/**
 * "Transferir Aporte & Desistir da Cobrança": o formando pagou parte da multa
 * do distrato e desapareceu. Repassa à turma o aporte já realizado e encerra a
 * geração de aporte deste distrato — as parcelas em aberto continuam ativas
 * (se ele pagar depois, entra como receita normal da empresa).
 *
 * O valor transferido é calculado pelo backend a partir da mesma fatia
 * (aporte ÷ multa) mostrada na coluna "Aporte à Turma"; aqui é só confirmação.
 */
export default function ModalDistratoWaive(props) {
  const { show, onHide, distrato, paidOnSlips, refreshCallback } = props;

  const { config } = useWrapper();
  const { formatReal } = useFormat();
  const canSubmit = usePermission("trainee", "distrato");

  const [description, setdescription] = useState("");
  const [isLoadingRequest, setisLoadingRequest] = useState(false);

  const _share = distrato?.aporte_share || 0;
  const _already_paid = distrato?.already_paid_total || 0;
  const _paid_on_slips = paidOnSlips || 0;
  const _realized = Math.min((_already_paid + _paid_on_slips) * _share, distrato?.aporte_value || 0);
  const _transferred = distrato?.aporte_transferred_value || 0;
  const _to_transfer = Math.max(_realized - _transferred, 0);

  const closeModal = () => {
    if (isLoadingRequest) return;
    setdescription("");
    onHide();
  };

  const sendWaive = async () => {
    setisLoadingRequest(true);
    try {
      const { data } = await api.post(`distratos/${distrato?.id}/waive`, { description }, config);
      toast.success(
        data?.transferred > 0
          ? `Aporte de ${formatReal(data.transferred)} transferido para a turma.`
          : "Cobrança encerrada. Não havia aporte realizado para transferir.",
      );
      refreshCallback && refreshCallback();
      setdescription("");
      onHide();
    } catch (__error) {
      const _message = __error?.response?.data?.error?.message;
      toast.error(_message || "Problemas ao transferir o aporte.");
      console.log("__error", __error);
    } finally {
      setisLoadingRequest(false);
    }
  };

  return (
    <ModalCustom show={show} onHide={closeModal} disableClose={isLoadingRequest} id="modal_distrato_waive" size="lg">
      <ModalCustom.Header>
        <ModalCustom.Title>Transferir Aporte &amp; Desistir da Cobrança</ModalCustom.Title>
      </ModalCustom.Header>
      <ModalCustom.Body>
        <p className="mb-2">
          <b>Atenção:</b> o aporte já realizado será lançado para a turma agora e este distrato deixa de gerar aporte.
          Se o formando pagar as parcelas restantes depois, o valor entra como receita da empresa e{" "}
          <b>nada mais será repassado à turma</b>. Essa ação não pode ser desfeita.
        </p>
        <p className="small-label mb-3">
          As parcelas em aberto <b>não</b> são canceladas e o acesso do formando continua limitado, para que ele ainda
          possa pagar.
        </p>
        <Row>
          <Col lg={6}>
            <StatusFlag status="Pagamento">Repasse Parcial à Turma</StatusFlag>
            <p className="small-label mt-2 mb-0">
              A fatia da turma é {(_share * 100).toFixed(2).replace(".", ",")}% de cada real da multa (aporte de{" "}
              {formatReal(distrato?.aporte_value || 0)} sobre multa de {formatReal(distrato?.multa_value || 0)}) — a
              mesma usada na coluna "Aporte à Turma".
            </p>
          </Col>
          <Col lg={6}>
            <Results
              rows={[
                { label: "Já Pago Antes do Distrato", value: formatReal(_already_paid) },
                { label: "Pago nas Parcelas da Multa", value: formatReal(_paid_on_slips) },
                ...(_transferred > 0
                  ? [{ label: "Aporte Já Transferido", value: formatReal(_transferred) }]
                  : []),
                {
                  label: <b>Aporte a Transferir Agora</b>,
                  value: <b>{formatReal(_to_transfer)}</b>,
                  type: "dark",
                },
              ]}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <span>Observação</span>
            <InputText
              disabled={isLoadingRequest}
              value={description}
              onChange={__ev => setdescription(__ev.target.value)}
              placeholder="Ex: Cobrança encaminhada para a via judicial"
            />
          </Col>
        </Row>
      </ModalCustom.Body>
      {canSubmit && (
        <ModalCustom.Footer>
          <Button disabled={isLoadingRequest} color="error_ghost" onClick={closeModal}>
            Cancelar
          </Button>
          <Button disabled={isLoadingRequest} loading={isLoadingRequest} color="error" onClick={sendWaive}>
            Transferir Aporte &amp; Desistir
          </Button>
        </ModalCustom.Footer>
      )}
    </ModalCustom>
  );
}
